import { getCallbacks, setState, resetState } from './state';
import { isValidPaymentMessage, createPaymentResult, lockBodyScroll, unlockBodyScroll } from './utils';
import { getPaymentUrl, isSandboxMode } from './config';

const MODAL_ID = 'arto-pay-payment-modal';
const BACKDROP_ID = 'arto-pay-payment-backdrop';
const CONTAINER_ID = 'arto-pay-payment-container';
const IFRAME_ID = 'arto-pay-payment-iframe';
const CLOSE_BTN_ID = 'arto-pay-payment-close';
const SPINNER_ID = 'arto-pay-payment-spinner';

let messageHandler: ((event: MessageEvent) => void) | null = null;
let keyHandler: ((event: KeyboardEvent) => void) | null = null;

let initData: {
  clientSecret: string;
  publicKey: string;
  token?: string;
  orderId?: string;
  origin: string;
} | null = null;

/* =========================
   Helpers
========================= */

function getStateInitData() {
  if (!initData) throw new Error('Init data not set');
  return initData;
}

function sendInitToIframe() {
  const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
  if (!iframe || !iframe.contentWindow || !initData) return;
  iframe.contentWindow.postMessage(
    {
      type: 'INIT',
      clientSecret: initData.clientSecret,
      publicKey: initData.publicKey,
      token: initData.token,
      orderId: initData.orderId,
    },
    initData.origin
  );
}

/* =========================
   Styles
========================= */

function injectStyles(): void {
  if (document.getElementById('opg-sdk-styles')) return;

  const style = document.createElement('style');
  style.id = 'opg-sdk-styles';
  style.textContent = `
    #${BACKDROP_ID} {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      z-index: 999998;
    }

    #${CONTAINER_ID} {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      max-width: 600px;
      height: 100%;
      max-height: 90vh;
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      z-index: 999999;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      transition: height 0.2s ease;
    }

    #${CONTAINER_ID} .sandbox-mode-banner {
      background: #FCA604;
      color: white;
      font-size: 16px;
      font-weight: 500;
      text-align: center;
      padding: 16px 8px;
      letter-spacing: 0.5px;
    }

    #${CLOSE_BTN_ID} {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      border: none;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 50%;
      cursor: pointer;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: bold;
      color: #333;
      line-height: 1;
    }

    #${CLOSE_BTN_ID}:hover {
      background: rgba(0, 0, 0, 0.2);
    }

    #${IFRAME_ID} {
      width: 100%;
      height: 100%;
      border: none;
      flex: 1;
    }

    #${SPINNER_ID} {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      border: 3px solid #f3f3f3;
      border-top: 3px solid #3498db;
      border-radius: 50%;
      animation: opg-spin 1s linear infinite;
    }

    @keyframes opg-spin {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }

    #${SPINNER_ID}.hidden {
      display: none;
    }
  `;
  document.head.appendChild(style);
}

/* =========================
   Message Handler
========================= */

function handleMessage(event: MessageEvent): void {
  const callbacks = getCallbacks();
  const container = document.getElementById(CONTAINER_ID);
  const iframe = document.getElementById(IFRAME_ID);

  if (event.data?.type === "IFRAME_HEIGHT") {
    const height = event.data.height
    if (!container) return;

    const banner = container.querySelector('.sandbox-mode-banner') as HTMLElement | null;
    const bannerHeight = banner ? banner.offsetHeight : 0;
    const finalHeight = height + bannerHeight;

    container.style.height = `${finalHeight}px`;
    return;
  }

  if (event.data?.type === 'URL_CHANGE') {
    iframe?.removeAttribute('scrolling')
    return;
  }


  // iframe bilang siap
  if (event.data?.type === 'READY') {
    sendInitToIframe();
    return;
  }

  if (event.data?.type === 'COPY_TEXT') {
    navigator.clipboard.writeText(event.data.value);
    return;
  }

  if (event.data?.type === 'PAYMENT_SUCCESS') {
    callbacks.onSuccess?.({
      transactionStatus: 'settlement',
      status: 'success',
      rawData: event.data,
    });
    closeModal(false);
    return;
  }

  if (event.data?.type === 'PAYMENT_RESULT') {
    const status = event.data.status;

    callbacks.onError?.({
      transactionStatus: status,
      status: 'error',
      rawData: event.data,
    });

    closeModal(false);
    return;
  }

  if (!isValidPaymentMessage(event.data)) return;

  const result = createPaymentResult(event.data);

  switch (result.status) {
    case 'success':
      callbacks.onSuccess?.(result);
      break;
    case 'pending':
      callbacks.onPending?.(result);
      break;
    case 'error':
      callbacks.onError?.(result);
      break;
  }
}

/* =========================
   UI Handlers
========================= */

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') closeModal(true);
}

/* =========================
   Public API
========================= */

export function createModal(
  clientSecret: string,
  paymentId: string,
  publicKey: string,
  token?: string,
  orderId?: string
): void {
  const url = getPaymentUrl(paymentId);
  const origin = new URL(url).origin;

  initData = { clientSecret, publicKey, token, orderId, origin };

  if (document.getElementById(MODAL_ID)) return;

  injectStyles();
  lockBodyScroll();

  const modal = document.createElement('div');
  modal.id = MODAL_ID;

  const backdrop = document.createElement('div');
  backdrop.id = BACKDROP_ID;

  const banner = document.createElement('div');

  if (isSandboxMode()) {
    banner.className = 'sandbox-mode-banner';
    banner.textContent = 'Test Mode – Simulated Payment';
  }

  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  
  if (isSandboxMode()) {
    container.style.maxHeight = 'calc(90vh + 50px)';
  }

  const closeBtn = document.createElement('button');
  closeBtn.id = CLOSE_BTN_ID;
  closeBtn.type = 'button';
  closeBtn.textContent = '✕';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.addEventListener('click', () => closeModal(true));

  const spinner = document.createElement('div');
  spinner.id = SPINNER_ID;

  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_ID;
  iframe.src = url;
  iframe.setAttribute('allow', 'payment; clipboard-write');

  iframe.addEventListener('load', () => {
    spinner.classList.add('hidden');
    sendInitToIframe(); // fallback kalau READY tidak ke-trigger
  });

  container.appendChild(banner)
  container.appendChild(closeBtn);
  container.appendChild(spinner);
  container.appendChild(iframe);
  modal.appendChild(backdrop);
  modal.appendChild(container);
  document.body.appendChild(modal);

  messageHandler = handleMessage;
  keyHandler = handleKeydown;

  window.addEventListener('message', messageHandler);
  document.addEventListener('keydown', keyHandler);

  setState({ isOpen: true, url });
}

export function closeModal(triggerOnClose = true): void {
  const callbacks = getCallbacks();
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
  }

  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }

  modal.remove();
  unlockBodyScroll();
  resetState();

  if (triggerOnClose) {
    callbacks.onClose?.();
  }
}
